// =========================================================
//                  1. CONFIGURATION 
// =========================================================

const CUTOFF_HOUR = 14; // Order cutoff time (2 PM)
const CUTOFF_MINUTE = 0;
const BANK_HOLIDAYS_API = 'https://www.gov.uk/bank-holidays.json'; // Official UK Bank Holidays API

let UK_HOLIDAYS_MAP = {}; // Map to store holiday dates (key: date, value: holiday title)
const DIVISION = 'england-and-wales'; // Bank holidays for England and Wales
const DELIVERY_CUTOFF = new Date();
DELIVERY_CUTOFF.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0); 

// =========================================================
//                       2. HELPER FUNCTIONS 
// =========================================================

// Function to format the date for the main delivery message (e.g., 'Monday, 3 November')
const formatDate = (date) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('en-GB', options); 
};

// Function to format the date for single holiday entries (e.g., 'Monday, 3 November')
const formatDateHoliday = (date) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('en-GB', options); 
};

// Function to format the date numerically for merged weekend entries (e.g., '1 November')
const formatDateNumeric = (date) => {
    const options = { day: 'numeric', month: 'long' };
    return date.toLocaleDateString('en-GB', options); 
};

// Function to check if the date is a weekend (Saturday: 6, Sunday: 0)
const isWeekend = (date) => {
    const day = date.getDay();
    return day === 6 || day === 0;
};

// Function to get the bank holiday title for a given date, or null
const getHolidayTitle = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return UK_HOLIDAYS_MAP[dateString] || null;
};

// Function to get the test date from the URL parameter 'date' or the current date
const getCurrentOrTestDate = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const testDate = urlParams.get('date');
    
    if (testDate) {
        // Append a noon time to minimize timezone parsing errors
        return new Date(`${testDate} 12:00:00`); 
    }
    // Return the current browser time
    return new Date();
};

// =========================================================
//                  3. CALCULATION LOGIC
// =========================================================

const calculateDeliveryDate = (currentDate) => {
    let deliveryDate = new Date(currentDate);
    let rawDelayDates = []; 
    
    // 1. Initial shift (D+1 or D+2)
    const isAfterCutoff = currentDate.getTime() >= DELIVERY_CUTOFF.getTime();
    
    if (isAfterCutoff) {
        deliveryDate.setDate(deliveryDate.getDate() + 2);
    } else {
        deliveryDate.setDate(deliveryDate.getDate() + 1);
    }

    // 2. Adjustment for weekends and holidays (find the first working day)
    let safetyCounter = 0;
    let weekendStartDate = null; 

    while (safetyCounter < 10) { 
        const isDOWeekend = isWeekend(deliveryDate);
        const holidayTitle = getHolidayTitle(deliveryDate);

        if (isDOWeekend) {
            // Weekend grouping logic
            if (!weekendStartDate) {
                weekendStartDate = new Date(deliveryDate);
            }
        } else {
            // If it's not a weekend, but a weekend was in progress, group and save it
            if (weekendStartDate) {
                const weekendEndDate = new Date(deliveryDate);
                // Go back one day to get the actual end of the weekend (Sunday)
                weekendEndDate.setDate(weekendEndDate.getDate() - 1); 

                rawDelayDates.push({
                    type: 'Weekend',
                    start: weekendStartDate,
                    end: weekendEndDate 
                });
                weekendStartDate = null;
            }
            
            if (holidayTitle) {
                // It's a Bank Holiday
                 rawDelayDates.push({
                    type: 'Holiday',
                    start: new Date(deliveryDate),
                    title: holidayTitle 
                });
            } else {
                break; // Found a working day
            }
        }
        
        deliveryDate.setDate(deliveryDate.getDate() + 1); 
        safetyCounter++;
    }
    
    // 3. Final conversion to a readable list
    const delayReasons = rawDelayDates.map(item => {
        if (item.type === 'Weekend') {
            // Format: "1 November - 2 November: Weekend" (Now uses formatDateNumeric)
            return `${formatDateNumeric(item.start)} - ${formatDateNumeric(item.end)}: Weekend`;
        } else if (item.type === 'Holiday') {
            // Format: "Monday, 5 May: Early May Bank Holiday (Bank Holiday)" (Uses full format)
            return `${formatDateHoliday(item.start)}: ${item.title} (Bank Holiday)`;
        }
    });

    return {
        date: deliveryDate,
        reasons: delayReasons
    };
};

// =========================================================
//                    4. INTERFACE / REFRESH
// =========================================================

const updateDeliveryInfo = () => {
    const infoElement = document.getElementById('delivery-info');
    const reasonElement = document.getElementById('delay-reason');
    const deliveryBox = document.querySelector('.delivery-box');
    
    const isTwoManDelivery = deliveryBox && deliveryBox.hasAttribute('data-twoman');

    const isTestMode = new URLSearchParams(window.location.search).has('date');

    if (Object.keys(UK_HOLIDAYS_MAP).length === 0) {
         infoElement.innerHTML = "<p>Loading holiday data...</p>";
         setTimeout(updateDeliveryInfo, 500);
         return;
    }
    
    const now = getCurrentOrTestDate();
    
    const result = calculateDeliveryDate(now);
    const deliveryDateFormatted = formatDate(result.date);
    
    const cutoffTimeToday = new Date(now);
    cutoffTimeToday.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);
    const timeRemainingMs = cutoffTimeToday.getTime() - now.getTime();
    
    let infoHTML = '';
    if (isTwoManDelivery) {
        infoHTML = `Earliest available delivery date is <strong>${deliveryDateFormatted}</strong>.`;
        
        if (!isTestMode) {
             setTimeout(updateDeliveryInfo, 60000); 
        }

    } else {
        const minutesRemaining = Math.floor(timeRemainingMs / (1000 * 60));
        const hours = Math.floor(minutesRemaining / 60);
        const minutes = minutesRemaining % 60;
        const formattedMinutes = minutes.toString().padStart(2, '0');
        
        let timerText = '';
        
        if (timeRemainingMs > 0) {
            timerText = hours > 0 
                ? `${hours} hrs ${formattedMinutes} mins`
                : `${minutes} mins`;
                
            infoHTML = `Delivery <strong>${deliveryDateFormatted}</strong>. Order within <span>${timerText}</span>.`;
            
            // Refresh every minute
            if (!isTestMode) {
                 const timeToNextMinute = 60000 - (now.getTime() % 60000);
                 setTimeout(updateDeliveryInfo, timeToNextMinute);
            }
            
        } else {
            // Cutoff passed (INACTIVE)
            infoHTML = `Delivery <strong>${deliveryDateFormatted}</strong>.`;
            
            // Refresh every minute (only in production mode)
            if (!isTestMode) {
                setTimeout(updateDeliveryInfo, 60000); 
            }
        }
    }
    
    // Wstawienie finalnego HTML
    infoElement.innerHTML = infoHTML;
    
    // Display delay reasons
    if (result.reasons.length > 0) {
        reasonElement.style.display = 'block';
        reasonElement.innerHTML = `<strong>Note:</strong> Delivery date adjusted due to non-working days:<br>
            <ul>${result.reasons.map(reason => `<li>${reason}</li>`).join('')}</ul>`;
    } else {
        reasonElement.style.display = 'none';
    }
};

// =========================================================
//                  5. INITIALIZATION
// =========================================================

// Function to fetch the list of Bank Holidays from the GOV.UK API
const fetchHolidays = async () => {
    try {
        const response = await fetch(BANK_HOLIDAYS_API);
        const data = await response.json();
        
        const events = data[DIVISION].events;
        
        // Build the holidays map
        events.forEach(event => {
            UK_HOLIDAYS_MAP[event.date] = event.title;
        });
        
        // Update the interface after data is loaded
        updateDeliveryInfo();
        
    } catch (error) {
        // In case of API error, use only weekend logic
        document.getElementById('delivery-info').innerHTML = "<p>Holiday data loading error (weekend logic only).</p>";
        updateDeliveryInfo(); 
    }
};

fetchHolidays();